import { useEffect, useRef } from 'react'
import { format, getHours, getMinutes, isSameDay } from 'date-fns'
import { cn } from '@/lib/utils'
import { formatTime, gridYToMinutes, minutesToGridY } from './calendarHelpers'
import { COMPRESSED_NIGHT_HEIGHT, DAY_START_HOUR, MINUTES_PER_SLOT, NIGHT_START_HOUR, SLOT_HEIGHT, SLOTS_PER_HOUR } from './constants'

export interface TimeGridProps {
  /** 当前列日期 */
  date: Date
  /** 列标题，不传则不显示 */
  header?: React.ReactNode
  /** 是否显示列标题（默认 true） */
  showHeader?: boolean
  /** 是否显示左侧时间刻度 */
  showTimeLabels?: boolean
  /** 当前列是否独立滚动（默认 true）。关闭时由父级滚动容器统一滚动 */
  scrollable?: boolean
  /** 是否将 22:00-08:00 压缩为一格（仅周视图使用） */
  compressedNight?: boolean
  /** 自定义事件节点 */
  children?: React.ReactNode
  /** 点击空白时段：参数为日期和 HH:mm */
  onSlotClick?: (date: Date, time: string) => void
  /** 放置拖拽事件：参数为日期、HH:mm 以及 dragStart 携带的 payload */
  onDrop?: (date: Date, time: string, payload?: unknown) => void
  /** 长按空白（移动端） */
  longPressHandlers?: {
    onTouchStart: (e: React.TouchEvent) => void
    onTouchMove: (e: React.TouchEvent) => void
    onTouchEnd: (e: React.TouchEvent) => void
  }
  /** 滚动事件（用于周视图多列同步） */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  /** 将内部滚动容器暴露给父组件 */
  onGridRef?: (el: HTMLDivElement | null) => void
}

const TOTAL_MINUTES = 24 * 60
const NORMAL_DAY_START = DAY_START_HOUR * 60
const NORMAL_DAY_END = NIGHT_START_HOUR * 60

export function TimeGrid({
  date,
  header,
  showHeader = true,
  showTimeLabels = false,
  scrollable = true,
  compressedNight = false,
  children,
  onSlotClick,
  onDrop,
  longPressHandlers,
  onScroll,
  onGridRef,
}: TimeGridProps) {
  const gridRef = useRef<HTMLDivElement | null>(null)
  const setGridRef = (el: HTMLDivElement | null) => {
    gridRef.current = el
    onGridRef?.(el)
  }

  // 首次加载时滚动到开始营业时间（仅在当前列独立滚动时生效）
  useEffect(() => {
    const el = gridRef.current
    if (!el || !scrollable) return
    const startSlot = DAY_START_HOUR * SLOTS_PER_HOUR
    el.scrollTop = startSlot * SLOT_HEIGHT
  }, [scrollable])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (!gridRef.current || !onDrop) return
    const rect = gridRef.current.getBoundingClientRect()
    const y = Math.max(0, e.clientY - rect.top + gridRef.current.scrollTop)
    const minutes = gridYToMinutes(y, compressedNight)
    const raw = e.dataTransfer.getData('application/json')
    const payload = raw ? (JSON.parse(raw) as unknown) : undefined
    onDrop(date, formatTime(minutes), payload)
  }

  const slots: number[] = []
  if (compressedNight) {
    for (let m = NORMAL_DAY_START; m < NORMAL_DAY_END; m += MINUTES_PER_SLOT) {
      slots.push(m)
    }
    slots.push(NORMAL_DAY_END) // 22:00，代表 22:00-次日 08:00 的压缩格
  } else {
    for (let m = 0; m < TOTAL_MINUTES; m += MINUTES_PER_SLOT) {
      slots.push(m)
    }
  }
  const slotHeight = (minutes: number) =>
    compressedNight && minutes === NORMAL_DAY_END ? COMPRESSED_NIGHT_HEIGHT : SLOT_HEIGHT
  const totalHeight = slots.reduce((sum, m) => sum + slotHeight(m), 0)

  const now = new Date()
  const isToday = isSameDay(date, now)
  const nowMinutes = isToday ? getHours(now) * 60 + getMinutes(now) : -1
  const currentLineTop = isToday ? minutesToGridY(nowMinutes, compressedNight) : -1

  return (
    <div className="flex flex-col h-full border-r border-slate-200 last:border-r-0 min-w-0">
      {showHeader && header && <div className="h-12 border-b border-slate-200 flex items-center justify-center text-sm font-medium shrink-0">{header}</div>}
      <div
        ref={setGridRef}
        data-date={format(date, 'yyyy-MM-dd')}
        className={cn('relative flex-1', scrollable && 'overflow-y-auto', showTimeLabels && !compressedNight && 'pl-10')}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onScroll={onScroll}
        onContextMenu={(e) => e.preventDefault()}
        {...(longPressHandlers || {})}
      >
        {showTimeLabels && (
          <div className="absolute left-1 top-0 bottom-0 w-8">
            {Array.from({ length: 24 }, (_, h) => h).map((h) => (
              <div
                key={h}
                data-testid={`time-label-${h}`}
                className={cn(
                  'absolute right-0 text-xs text-slate-400 pr-1',
                  // 与周视图一致：标签贴在线上方；00:00 首标签保持线下方防裁切
                  h === 0 ? '' : '-translate-y-full',
                )}
                style={{ top: (h * 60 / MINUTES_PER_SLOT) * SLOT_HEIGHT + (h === 0 ? 2 : 0) }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        )}
        <div className="relative" style={{ height: totalHeight }}>
          {slots.map((minutes, idx) => (
            <div
              key={minutes}
              data-minutes={minutes}
              data-testid={`time-slot-${format(date, 'yyyy-MM-dd')}-${formatTime(minutes)}`}
              className={cn(
                'box-border',
                // border-b 位于"下一时刻"：半点槽的底边才是整点 → 整点粗线落在真实整点位置
                minutes % 60 === 30 ? 'border-b border-slate-300' : 'border-b border-slate-100',
                // 首槽顶边补整点粗线（网格起点的整点位置否则没有线）；
                // 用 border-t-slate-300 侧向写法，避免 tailwind-merge 把 border-b 的颜色合并掉
                idx === 0 && 'border-t border-t-slate-300',
                compressedNight && minutes === NORMAL_DAY_END && 'bg-slate-50',
              )}
              style={{ height: slotHeight(minutes) }}
              onClick={() => onSlotClick?.(date, formatTime(minutes))}
            />
          ))}
          {isToday && currentLineTop >= 0 && (
            <div
              data-testid="current-time-line"
              className="absolute left-0 right-0 border-t-2 border-red-500 pointer-events-none z-10"
              style={{ top: currentLineTop }}
            />
          )}
          {children}
        </div>
      </div>
    </div>
  )
}
